import { DemandManagementApiService } from '@api_patient/demand_management_api.service';
import { UnitsApiService } from '@api_patient/units_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { AuthService } from '@application/authentication/auth.service';
import { HttpStatusCode } from '@application/http.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { AsyncSignalValue } from '@component/async.component';
import { RouterService } from '@cortex-shim/router.service';
import { getAbsoluteRoute } from '@module/get_absolute_route';
import { signal } from '@preact/signals-react';
import { client as clientV2, RedRobinService, type StartSessionV2Input, Status } from '@visiba/api-vcs-client-v2';
import { ControllerProtocol, Injectable } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { NiN } from '@visiba/national-identity-number';
import dayjs from 'dayjs';
import type { State } from 'history';
import { RED_ROBIN_SUPPORTED_LANGUAGES } from '../../constants';
import { CareNavigationService } from '../view_care_navigation/care_navigation.service';

export enum RedRobinScreen {
	WhoIsRespondant = '',
	InformationGathering = 'information-gathering',
	NhsLoginRequired = 'nhs-login-required',
	Triage = 'triage',
	Closed = 'closed',
	Error = 'error',
}

type ClosedReason = 'OutsideMonitoredHours' | 'CapLimitReached' | 'Other';
interface FlowData {
	currentCountry: keyof NiN.Implementations;
	allowRepresentative: boolean;
	closedReason: ClosedReason | null;
}

@Injectable()
export class ViewRedRobinController implements ControllerProtocol {
	public flowData = signal<AsyncSignalValue<FlowData>>({
		value: null,
		flowState: 'pending',
	});

	private readonly routeBase: string;

	private readonly logger = new Logger('ViewRedRobinController');

	constructor(
		public readonly unitService: UnitService,
		public readonly routerService: RouterService,
		public readonly universalConfig: UniversalConfig,
		private readonly unitsApiService: UnitsApiService,
		private readonly authService: AuthService,
		private readonly careNavigationService: CareNavigationService,
		private readonly analyticService: AnalyticService,
		private readonly demandManagementApiService: DemandManagementApiService,
	) {
		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);
		this.routeBase = unit.routeWithoutLicenceholder;
	}

	async whenMount() {
		this.analyticService.trackPageView({ customTitle: 'Red Robin' });

		this.logger.logInformation('Mounting ViewRedRobinController');

		const unitConfigResult = await this.unitsApiService.getUnitConfig(this.universalConfig.selectedUnitId);
		if (unitConfigResult.err) return Err(unitConfigResult.err);

		const allowRepresentative = unitConfigResult.ok.data.Offer?.AllowClientAgents || false;
		const userNationality = this.authService.getUser()?.nationality;
		const currentCountry =
			userNationality && NiN.supportsCountryCode(userNationality)
				? (userNationality as keyof NiN.Implementations)
				: this.universalConfig.selectedCountryCode.peek();

		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);

		const triageStatus = await this.demandManagementApiService.getTriageOpenStatus(unit.receptionId);

		if (triageStatus.ok?.data.status === Status.Closed) {
			this.analyticService.trackEvent(
				AnalyticCategories.RedRobin,
				ViewRedRobinAnalyticActions.Closed,
				'Closed through demand management',
			);
			const closedReason = (triageStatus.ok?.data.closedReason ?? 'Other') as ClosedReason;
			this.flowData.value = {
				value: {
					currentCountry,
					allowRepresentative,
					closedReason,
				},
				flowState: 'accessible',
			};
			this.goToScreen(RedRobinScreen.Closed, { replace: true, closedReason });

			return;
		}

		this.flowData.value = {
			value: {
				currentCountry,
				allowRepresentative,
				closedReason: null,
			},
			flowState: 'accessible',
		};
	}

	// Red robin can't triage patients under one year old.
	public isBelowOneYearOld(identityNumber: string, country: keyof NiN.Implementations): boolean {
		const implementation = NiN.produce(country);
		const dateOfBirth = implementation.getDateOfBirth(implementation.normalize(identityNumber));

		return dateOfBirth !== null && dayjs().diff(dayjs(dateOfBirth), 'years') < 1;
	}

	public isAbove110YearsOld(identityNumber: string, identityCode: string, dateOfBirth?: string | null): boolean {
		let resolvedDateOfBirth: string | Date | null = dateOfBirth ?? null;
		if (resolvedDateOfBirth == null) {
			const family = identityCode.split(':')[0] ?? '';
			if (!NiN.supportsCountryCode(family)) return false;

			const implementation = NiN.produce(family);
			resolvedDateOfBirth = implementation.getDateOfBirth(implementation.normalize(identityNumber));
		}

		return resolvedDateOfBirth !== null && dayjs().diff(dayjs(resolvedDateOfBirth), 'years') > 110;
	}

	public goToReceptionHome() {
		// Client-side routerService.navigateByUrl leaves this flow's route tree stuck rendered
		// after leaving a flow route (e.g. this triage flow); a real URL change reliably resets it.
		// The router's basename (licence holder alias) isn't applied to window.location, so it's prepended here.
		window.location.href = getAbsoluteRoute([this.universalConfig.alias, this.routeBase].filter(Boolean).join('/'));
	}

	public goToScreen(
		screen: RedRobinScreen,
		options: {
			replace?: boolean;
			sessionId?: string;
			closedReason?: ClosedReason;
			state?: State;
		} = {},
	): void {
		let path = '';
		const analyticActionName = screen;

		switch (screen) {
			case RedRobinScreen.WhoIsRespondant:
				path = '';
				break;
			case RedRobinScreen.InformationGathering:
				path = 'information-gathering';
				break;
			case RedRobinScreen.NhsLoginRequired:
				path = 'nhs-login-required';
				break;
			case RedRobinScreen.Triage:
				path = `${options.sessionId}`;
				break;
			case RedRobinScreen.Error:
				path = 'error';
				break;
			case RedRobinScreen.Closed:
				path = 'closed';
				this.updateFlowData({ closedReason: options.closedReason ?? null });

				break;
		}

		this.analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.GoToScreen, analyticActionName);
		const url = [this.routeBase, 'triage', path].filter(Boolean);

		this.routerService.navigateByUrl(getAbsoluteRoute(url.join('/')), {
			replace: options.replace,
			state: options.state,
		});
	}

	public getFlowData(): FlowData {
		if (this.flowData.value?.flowState === 'accessible') {
			return this.flowData.value.value;
		}

		throw new Error("FlowData isn't accessible yet. Make sure to run whenMount() before trying to access flowData.");
	}

	public updateFlowData(data: Partial<FlowData>): void {
		if (this.flowData.value.value === null) {
			return;
		}

		this.flowData.value = {
			value: {
				...this.flowData.value.value,
				...data,
			},
			flowState: 'accessible',
		};
	}

	public async createSession(input: Omit<StartSessionV2Input, 'LanguageCode'>, state?: State): Promise<void> {
		const normalizeRedRobinLanguageCode = (languageCode: string): string =>
			languageCode.toLowerCase() === 'nb' ? 'no' : languageCode;
		const selectedLanguageCode = normalizeRedRobinLanguageCode(this.universalConfig.selectedLanguageCode.peek());
		const languageCode = RED_ROBIN_SUPPORTED_LANGUAGES.includes(selectedLanguageCode)
			? selectedLanguageCode
			: (this.universalConfig.availableLanguages
					.map(normalizeRedRobinLanguageCode)
					.find((language) => RED_ROBIN_SUPPORTED_LANGUAGES.includes(language)) ?? 'en');
		const body = {
			LanguageCode: languageCode,
			...input,
		} satisfies StartSessionV2Input;

		try {
			const result = await RedRobinService.redRobinStartSessionV2({
				client: clientV2,
				path: { unitId: this.universalConfig.selectedUnitId },
				body,
			});

			if (result.error !== undefined) {
				const errorData = result.error;

				if (result.response.status === HttpStatusCode.BadRequest_400 && errorData.title === 'Service is closed') {
					this.analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.Closed, 'Session create');
					const closedReason = errorData.detail as ClosedReason;
					this.goToScreen(RedRobinScreen.Closed, { replace: true, closedReason });

					return;
				}

				if (result.response.status === HttpStatusCode.BadRequest_400 && errorData.title === 'Age not within scope') {
					this.analyticService.trackEvent(
						AnalyticCategories.RedRobin,
						ViewRedRobinAnalyticActions.PatientUnderOneYearOfAge,
						'Session create',
					);

					const patientNin = input.AsUnverifiedRepresentativeForNin;
					const resolvedDateOfBirth = input.DateOfBirth ?? (patientNin ? this.getDateOfBirthFromNin(patientNin) : null);
					this.careNavigationService.setCurrentPatientDateOfBirth(resolvedDateOfBirth);

					this.goToCareNavigationDescriptionOfMatter({
						replace: true,
						state,
						patientNin,
					});

					return;
				}

				this.setSessionCreationError();
				return;
			}
			this.analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.SessionCreated);
			this.goToScreen(RedRobinScreen.Triage, {
				replace: true,
				sessionId: result.data.PatientEntryUrl.split('/').pop(),
				state,
			});
		} catch {
			this.setSessionCreationError();
		}
	}

	private setSessionCreationError(): void {
		this.analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.ErrorCreatingSession);
		this.flowData.value = {
			value: null,
			flowState: 'error',
		};
	}

	/** NIN-based countries (SE/NO/FI) encode the date of birth in the identity number itself. */
	private getDateOfBirthFromNin(nin: string): string | null {
		const implementation = NiN.produce(this.getFlowData().currentCountry);
		const dateOfBirth = implementation.getDateOfBirth(implementation.normalize(nin));

		return dateOfBirth ? dayjs(dateOfBirth).format('YYYY-MM-DD') : null;
	}

	public goToMessaging(patientNin: Nullable<string>, state?: State): void {
		const queryAddition = patientNin ? `?nin=${patientNin}` : '';
		this.routerService.navigateByUrl(`../message${queryAddition}`, { replace: true, state });

		// TODO: Remove when angular is gone!
		if (
			(window as typeof window & { __TEMP__overrideNavigation: Record<string, unknown> | undefined })['__TEMP__overrideNavigation'] !=
			null
		) {
			window.location.reload();
		}
	}

	public goToCareNavigationDescriptionOfMatter(options: { replace?: boolean; state?: State; patientNin?: string | null } = {}) {
		if ('patientNin' in options) {
			this.careNavigationService.setCurrentPatientNin(options.patientNin ?? null);
		}

		this.routerService.navigateByUrl(this.careNavigationService.getDescribeMatterRoute({ preserveSubjectContext: true }), {
			replace: options.replace,
			state: options.state,
		});

		// TODO: Remove when angular is gone!
		if (
			(window as typeof window & { __TEMP__overrideNavigation: Record<string, unknown> | undefined })['__TEMP__overrideNavigation'] !=
			null
		) {
			window.location.reload();
		}
	}
}

export enum ViewRedRobinAnalyticActions {
	GoToScreen = 'Go-to-screen',
	Closed = 'Closed through demand management',
	NotAllowedToBeHere = 'Not allowed to be here',
	DoesNotAllowRepresentative = 'Does not allow representative',
	PatientUnderOneYearOfAge = 'The patient is under one year of age',
	FormIsInvalid = 'Form is invalid',
	SessionCreated = 'Session sucessfully created',
	ErrorCreatingSession = 'Error while creating session',
	AutoSetRespondentTypeRepresentative = 'Auto set respondent type to representative',
}
